import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'

import type {
  APIBookingDraft,
  APIBookingDraftBusService,
  APIBookingDraftBusServiceOption,
  APIBookingDraftRoom,
} from '@/scopes/checkout/infrastructure/api/types'

import { isNil, values } from 'ramda'
import {
  type CheckoutBusService,
  type CheckoutBusServiceOption,
  type ServiceMapper,
  ServiceType,
} from '../../../domain/types'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceOptionMapper?: ServiceOptionMapper
  serviceType: ServiceType
}

export const createBusServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutBusService> = (
  dependencies: Dependencies,
) => {
  const { serviceName, serviceTypeMapper, serviceOptionMapper = createServiceOptionMapper(), serviceType } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftBusService

    if (!service)
      return null

    const { aName, options } = service as unknown as { aName: string, options: APIBookingDraftBusServiceOption[] }

    const outboundOptions = options.filter(({ direction }) => direction?.toLowerCase() === 'outbound')
    const inboundOptions = options.filter(({ direction }) => direction?.toLowerCase() === 'inbound')
    const participants
      = bookingDraft?.participants.reduce((acc, participant) => {
        const selectedValueOutbound
          = buildUniqOptionId(
            options
              .filter(({ isSelected }) => isSelected)
              .filter(({ direction }) => direction?.toLowerCase() === 'outbound')
              .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id)),
          ) || null
        const selectedValueInbound
          = buildUniqOptionId(
            options
              .filter(({ isSelected }) => isSelected)
              .filter(({ direction }) => direction?.toLowerCase() === 'inbound')
              .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id)),
          ) || null
        const selectedValueNoneOption
          = buildUniqOptionId(
            options
              .filter(({ isSelected }) => isSelected)
              .filter(({ isNoneOption }) => isNoneOption)
              .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id)),
          ) || null
        return {
          ...acc,
          [participant.id.toString()]: {
            id: participant.id.toString(),
            name: participant.name || participant.alternativeName?.toLowerCase() || '',
            roomId: participant.roomId.toString(),
            selectedValueOutbound,
            selectedValueInbound,
            selectedValueNoneOption,
            availableOptions: options
              .filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map(option => buildUniqOptionId(option)),
          },
        }
      }, {}) ?? {}

    const anyParticipantHasSelectedValue = values(participants).some(
      ({ selectedValueOutbound }) => selectedValueOutbound,
    )

    return {
      id: aName,
      isSelected: anyParticipantHasSelectedValue ?? !isNil(anyParticipantHasSelectedValue),
      type: ServiceType.Bus,
      rooms: bookingDraft?.rooms.map(mapRoom) ?? [],
      participants,
      noneOptions:
        options && options.filter(({ isNoneOption }) => isNoneOption).length > 0
          ? options
              .filter(({ isNoneOption }) => isNoneOption)
              .reduce(
                (acc2: Record<string, CheckoutBusServiceOption>, option) => ({
                  ...acc2,
                  [buildUniqOptionId(option)]: {
                    ...serviceOptionMapper.map(option, service, bookingDraft),
                    id: buildUniqOptionId(option),
                    mandatoryLinkedPickUpPoint: option.mandatoryLinkedPickUpPoint || '',
                  },
                }),
                {},
              )
          : {},
      outboundOptions:
        outboundOptions && outboundOptions.filter(({ isNoneOption }) => !isNoneOption).length > 0
          ? outboundOptions.reduce(
              (acc2: Record<string, CheckoutBusServiceOption>, option) => ({
                ...acc2,
                [buildUniqOptionId(option)]: {
                  ...serviceOptionMapper.map(option, service, bookingDraft),
                  id: buildUniqOptionId(option),
                  mandatoryLinkedPickUpPoint: option.mandatoryLinkedPickUpPoint || '',
                },
              }),
              {},
            )
          : {},
      inboundOptions:
        inboundOptions && inboundOptions.filter(({ isNoneOption }) => !isNoneOption).length > 0
          ? inboundOptions.reduce(
              (acc2: Record<string, CheckoutBusServiceOption>, option) => ({
                ...acc2,
                [buildUniqOptionId(option)]: {
                  ...serviceOptionMapper.map(option, service, bookingDraft),
                  id: buildUniqOptionId(option),
                  mandatoryLinkedPickUpPoint: option.mandatoryLinkedPickUpPoint || '',
                },
              }),
              {},
            )
          : {},
    }
  }

  function buildUniqOptionId(option: APIBookingDraftBusServiceOption | undefined) {
    if (!option)
      return ''
    return option.mandatoryLinkedPickUpPoint
      ? `${option.code}??${option.mandatoryLinkedPickUpPoint}`
      : `${option.code}`
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map(participantId => participantId.toString()),
    }
  }

  function mapSelection(service: CheckoutBusService) {
    const { participants } = service
    return Object.values(participants ?? {})
      .map(({ id, selectedValueOutbound }) => {
        const selectedOutboundOption = selectedValueOutbound ? service.outboundOptions[selectedValueOutbound] : null
        const noneOption = selectedValueOutbound ? service.noneOptions[selectedValueOutbound] : null
        return [
          {
            participantId: id,
            value: selectedOutboundOption?.value || noneOption?.value || null,
            mandatoryLinkedPickUpPoint:
              selectedOutboundOption?.mandatoryLinkedPickUpPoint || noneOption?.mandatoryLinkedPickUpPoint || null,
          },
        ]
      })
      .flat()
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType)
  }

  function mapSelectedItemToGA4(service: CheckoutBusService, step: number): ImpressionViewAncillariesItem[] {
    const inputs: SelectedItemInput[] = []
    const categories: string[] = []
    const category2s: string[] = []
    const seenItemIds = new Set<string>()

    values(service.participants ?? {}).forEach((participant) => {
      [
        {
          option: participant.selectedValueOutbound
            ? service.outboundOptions[participant.selectedValueOutbound] ?? service.noneOptions[participant.selectedValueOutbound]
            : null,
          direction: participant.selectedValueOutbound && service.outboundOptions[participant.selectedValueOutbound]
            ? 'Outbound'
            : 'NoneOption',
        },
        {
          option: participant.selectedValueInbound ? service.inboundOptions[participant.selectedValueInbound] : null,
          direction: 'Inbound',
        },
      ].forEach(({ option, direction }) => {
        if (!option || seenItemIds.has(option.id))
          return

        seenItemIds.add(option.id)
        inputs.push({
          label: option.label,
          value: option.id,
          price: option.price,
          rawPrice: option.rawPrice,
          badge: option.badge,
        })
        categories.push(`Ancillary > ${option.badge ? 'Persuasion' : 'Additional cost'} > ${direction}`)
        category2s.push(direction)
      })
    })

    return mapDefaultSelectedItemToGA4(inputs, {
      serviceType,
      serviceId: service.id,
      step,
    }).map((item, index) => ({
      ...item,
      item_category: categories[index],
      item_category2: category2s[index],
    }))
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
